import { useEffect } from "react";
import { Typography, Box, Paper } from "@mui/material";
import PowerSupplyForm from "./PowerSupply.tsx";
import { ElectronicsConfiguration } from "../../models/electronicsConfiguration.ts";
import SignalMapping from "./SignalMapping.tsx";
import CommunicationForm from "./Communication.tsx";
import FlasherForm from "./Flasher.tsx";
import { Signal } from "../../models/signal.ts";
import ResistorSignalTable from "./ResistorSignalTable.tsx"
import SchottkySignalTable from "./SchottkySignalTable.tsx";
import { useElectronicsConfiguration } from "../../context/ElectronicsConfigurationContext.tsx";
import { useValidateElectronics } from "../../hooks/useValidateElectronics.ts"
import { useElectronicModules } from "../../hooks/useElectronicModules.ts";
import PageLayout from '../../components/PageLayout.tsx';
import isEqual from 'fast-deep-equal';
import { isDisplayableSignal } from "../../utils/signalUtils.ts";

const ElectronicsConfigurationPage = () => {
  const { config, setConfig, handleChange } = useElectronicsConfiguration();
  const { electronicModules } = useElectronicModules();
  const { validateElectronics } = useValidateElectronics();

  useEffect(() => {
    const updateModules = async () => {
      const modules = await electronicModules(config.signal_mapping);
      if (modules && !isEqual(modules, config.modules)) {
        setConfig({
          ...config,
          modules
        });
      }
    };

    updateModules();
  }, [config.signal_mapping]);


  useEffect(() => {
    validateElectronics(config);
  }, [config]);

  const getAllSignals = (config: ElectronicsConfiguration): Signal[] => {
    const signals: Signal[] = [...config.custom_signals.resistor, ...config.custom_signals.schottky, ...config.testpoints];
    signals.push(config.power_supply.ground);
    if (config.communication.rs232.enable) signals.push(config.communication.rs232.rx, config.communication.rs232.tx);
    if (config.communication.rs485.enable) signals.push(config.communication.rs485.a, config.communication.rs485.b);
    if (config.communication.uart.enable) signals.push(config.communication.uart.rx, config.communication.uart.tx);
    if (config.communication.i2c0.enable) signals.push(config.communication.i2c0.sda, config.communication.i2c0.scl);
    if (config.communication.i2c1.enable) signals.push(config.communication.i2c1.sda, config.communication.i2c1.scl);
    if (config.communication.usb.enable) signals.push(config.communication.usb.otg, config.communication.usb.dp, config.communication.usb.dn);
    if (config.communication.sd_card.enable) signals.push(config.communication.sd_card.clk, config.communication.sd_card.d0, config.communication.sd_card.d1, config.communication.sd_card.d2, config.communication.sd_card.d3);
    if (config.flasher.stlink1.enable) signals.push(config.flasher.stlink1.swclk_tck, config.flasher.stlink1.nrst, config.flasher.stlink1.ntrst, config.flasher.stlink1.swdio_tms, config.flasher.stlink1.swo_tdo, config.flasher.stlink1.target_voltage, config.flasher.stlink1.tdi);
    if (config.flasher.stlink2.enable) signals.push(config.flasher.stlink2.swclk_tck, config.flasher.stlink2.nrst, config.flasher.stlink2.ntrst, config.flasher.stlink2.swdio_tms, config.flasher.stlink2.swo_tdo, config.flasher.stlink2.target_voltage, config.flasher.stlink2.tdi);
    if (config.power_supply.dcdc24V.enable) signals.push(config.power_supply.dcdc24V);
    if (config.power_supply.dcdc12V.enable) signals.push(config.power_supply.dcdc12V);
    if (config.power_supply.dcdc5V.enable) signals.push(config.power_supply.dcdc5V);
    if (config.power_supply.dcdc3V3.enable) signals.push(config.power_supply.dcdc3V3);
    if (config.power_supply.linear1.enable) signals.push(config.power_supply.linear1);
    if (config.power_supply.linear2.enable) signals.push(config.power_supply.linear2);
    if (config.power_supply.linear3.enable) signals.push(config.power_supply.linear3);
    if (config.power_supply.dcdcAdjustable1.enable) signals.push(config.power_supply.dcdcAdjustable1);
    if (config.power_supply.dcdcAdjustable2.enable) signals.push(config.power_supply.dcdcAdjustable2);
    if (config.power_supply.dcdcAdjustable3.enable) signals.push(config.power_supply.dcdcAdjustable3);
    if (config.power_supply.labPowerSupply.enable) {
      signals.push(
        config.power_supply.labPowerSupply.channel1.positive,
        config.power_supply.labPowerSupply.ground,
      );
      if (config.power_supply.labPowerSupply.channels >= 2) {
        signals.push(
          config.power_supply.labPowerSupply.channel2.positive,
        );
      }
      if (config.power_supply.labPowerSupply.channels >= 3) {
        signals.push(
          config.power_supply.labPowerSupply.channel3.positive,
        );
      }
    }
    return signals.filter(isDisplayableSignal);
  };

  const syncSignalReferences = (
    config: ElectronicsConfiguration,
    updatedSignals: Signal[] = [],
  ): ElectronicsConfiguration => {
    if (updatedSignals.length === 0) return config;

    const replacements = new Map(updatedSignals.map((signal) => [signal.id, signal]));
    const replace = (signal: Signal): Signal => replacements.get(signal.id) ?? signal;

    return {
      ...config,
      custom_signals: {
        resistor: config.custom_signals.resistor.map((item) => ({
          ...item,
          source_signal: replace(item.source_signal),
        })),
        schottky: config.custom_signals.schottky.map((item) => ({
          ...item,
          source_signal: replace(item.source_signal),
        })),
      },
      signal_mapping: {
        ...config.signal_mapping,
        relais: config.signal_mapping.relais.map((item) => ({
          ...item,
          high_signal: replace(item.high_signal),
          low_signal: replace(item.low_signal),
        })),
        voltage: config.signal_mapping.voltage.map((item) => ({
          ...item,
          signal: replace(item.signal),
        })),
        current: config.signal_mapping.current.map((item) => ({
          ...item,
          high_signal: replace(item.high_signal),
          low_signal: replace(item.low_signal),
        })),
        two_quadrant: config.signal_mapping.two_quadrant.map((item) => ({
          ...item,
          signal: replace(item.signal),
        })),
        resistor: config.signal_mapping.resistor.map((item) => ({
          ...item,
          pin1: replace(item.pin1),
          pin2: replace(item.pin2),
        })),
      },
    };
  };

  return (
    <PageLayout
      title="Elektronik"
      backText="Zurück zur Mechanik"
      backTarget="/mechanics"
      nextText="Weiter zu den Prüfabläufen"
      nextTarget="/testprocedure"
    >
      <Paper sx={{ p: 3, mb: 6 }}>
        <PowerSupplyForm
          data={config.power_supply}
          onChange={(value) => { setConfig({ ...config, power_supply: value }); } }
        />
      </Paper>
      <Paper sx={{ p: 3, mb: 6 }}>
        <CommunicationForm
          data={config.communication}
          onChange={(value) => handleChange("communication", value)}
        />
      </Paper>
      <Paper sx={{ p: 3, mb: 6 }}>
        <FlasherForm
          data={config.flasher}
          onChange={(value) => handleChange("flasher", value)}
        />
      </Paper>
      <Paper sx={{ p: 3, mb: 6 }}>
        <Typography variant="h6" gutterBottom>Systemspezifische Netze</Typography>
        <ResistorSignalTable
          data={config.custom_signals.resistor}
          onChange={(value) => handleChange("custom_signals", { ...config.custom_signals, resistor: value})}
          signalOptions={getAllSignals(config)}
        />
      <Box mt={2} />
        <SchottkySignalTable
          data={config.custom_signals.schottky}
          onChange={(value) => handleChange("custom_signals", { ...config.custom_signals, schottky: value})}
          signalOptions={getAllSignals(config)}
        />
      </Paper>
      <Paper sx={{ p: 3, mb: 6 }}>
        <SignalMapping
          data={config.signal_mapping}
          onChange={(mapping) => { setConfig({ ...config, signal_mapping: mapping }); } }
          communication={config.communication}
          onCommunicationChange={(communication, updatedSignals) => {
            setConfig(syncSignalReferences({ ...config, communication }, updatedSignals));
          }}
          flasher={config.flasher}
          onFlasherChange={(flasher, updatedSignals) => {
            setConfig(syncSignalReferences({ ...config, flasher }, updatedSignals));
          }}
          signals={getAllSignals(config)}
        />
      </Paper>
    </PageLayout>
  );
};

export default ElectronicsConfigurationPage;
